import {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import GetHeroIcon from "../../../../../../utils/getHeroIcons";
import Button from "../../../../../../components/atoms/button";
import Modal from "../../../../../../components/atoms/modal";
import SelectCustom from "../../../../../../components/atoms/selectCustom";
import CameraPreviewLoader from "../../../../../../components/molecules/cameraPreviewLoader";
import CalibrationProcessStatusAlert from "../calibrationProcessStatusAlert";
import CalibrationInfoModal from "../../calibration-info-modal/calibrationInfoModal";

import { useAppDispatch, useAppSelector } from "../../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useStartAssessmentMutation,
  useUpdateSessionIdMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import {
  ProcessStatus,
  ProctoringFunctionsContext
} from "../../../../../../contexts/proctoringFunctionsContext";

import { requestFullScreen } from "@assessment/utils/global/fullScreenFunctions";
import { useShowErrors } from "../../../toasterWrapper";
import { generateUniqueId } from "../generateId";

interface Props {
  setActiveStep?: (val: "next" | "previous") => void;
  inResumeScreen?: boolean;
}

const SystemCheck = ({ setActiveStep, inResumeScreen = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showErrors = useShowErrors();

  const { scheduleId } = useParams();

  const [startAssessment, { isLoading }] = useStartAssessmentMutation();

  const context = useContext(ProctoringFunctionsContext);
  if (!context) {
    throw new Error("Context Error");
  }
  const {
    startCalibration,
    startProctoring,
    retryProctoring,
    registerPreviewSink,
    processStatus,
    sessionState,
    failureReason,
    canCalibrate,
    canStart,
    canRetry
  } = context;

  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [error, setError] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCalibrationIssueModal, setShowCalibrationIssueModal] =
    useState(false);
  const [calibrationTimeout, setCalibrationTimeout] = useState<boolean>(false);
  const [delayedMessage, setDelayedMessage] = useState(false);

  const [updateTabId] = useUpdateSessionIdMutation();

  const selectedCamera = useAppSelector(
    (state) => state.AssessmentSlice.proctoring.devices.camera
  );
  const selectedCameraRef = useRef(selectedCamera);

  useEffect(() => {
    selectedCameraRef.current = selectedCamera;
  }, [selectedCamera]);

  const tncEnabled = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.tncEnabled
  );

  const stepperFormActiveStep = useAppSelector(
    (state) => state.AssessmentSlice.preAssessment.stepperFormActiveStep
  );

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const browserProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.browserProctoring
  );

  const videoPreviewLoader = useAppSelector(
    (state) => state.AssessmentSlice?.videoPreviewLoader
  );

  const failureReasonMessage = useMemo(() => {
    switch (failureReason) {
      case "camera_permission_denied":
        return t(
          "assessment.systemCheck.failureReasons.cameraPermissionDenied"
        );
      case "camera_unavailable":
        return t("assessment.systemCheck.failureReasons.cameraUnavailable");
      case "facemesh_init_failed":
        return t("assessment.systemCheck.failureReasons.facemeshInitFailed");
      case "coco_load_failed":
        return t("assessment.systemCheck.failureReasons.cocoLoadFailed");
      case "stream_lost_reacquire_failed":
        return t(
          "assessment.systemCheck.failureReasons.streamLostReacquireFailed"
        );
      case "calibration_insufficient_data":
        return t(
          "assessment.systemCheck.failureReasons.calibrationInsufficientData"
        );
      case "detection_cadence_unavailable":
        return t(
          "assessment.systemCheck.failureReasons.detectionCadenceUnavailable"
        );
      default:
        return "";
    }
  }, [failureReason, t]);

  const requestPermissions = async () => {
    try {
      const permissionProbeStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { ideal: 640 },
          height: { ideal: 360 },
          frameRate: { ideal: 30 }
        }
      });
      permissionProbeStream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      setError(t("assessment.systemCheck.cameraPermissionDenied"));
      setShowErrorModal(true);
      console.error(err);
      return false;
    }
  };

  const getDevices = useCallback(async () => {
    try {
      dispatch(AssessmentSliceActions.mutateVideoPreviewLoader(true));
      const grantedPermission = await requestPermissions();
      if (!grantedPermission) {
        dispatch(AssessmentSliceActions.mutateVideoPreviewLoader(false));
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      setCameraDevices(cameras);

      if (cameras.length > 0) {
        const preferredCameraId =
          selectedCameraRef.current &&
          cameras.some(
            (camera) => camera.deviceId === selectedCameraRef.current
          )
            ? selectedCameraRef.current
            : cameras[0].deviceId;
        if (preferredCameraId !== selectedCameraRef.current) {
          dispatch(
            AssessmentSliceActions.mutateMediaDevices(preferredCameraId)
          );
        }
      } else {
        // No cameras found — clear the loader since no preview will appear
        dispatch(AssessmentSliceActions.mutateVideoPreviewLoader(false));
      }
    } catch (err) {
      dispatch(AssessmentSliceActions.mutateVideoPreviewLoader(false));
      setError(t("assessment.systemCheck.errorFetchingDevices"));
      console.error(err);
    }
  }, [dispatch, t]);

  const startAssessmentHandler = async (inResumeScreen: boolean) => {
    try {
      if (!scheduleId) {
        throw {
          status: "",
          title: t("assessment.scheduleIdNotFound"),
          err: {},
          description: ""
        };
      }

      if (!inResumeScreen) {
        const res = await startAssessment({
          scheduleId: scheduleId,
          tncAccepted: false,
          tncAcceptedAt: null
        });

        if (res.error) {
          throw {
            status: "",
            title: t("assessment.systemCheck.failedToStartAssessment"),
            err: res.error,
            description: ""
          };
        }

        const ongoingId = res?.data?.data?.id;

        if (ongoingId) {
          dispatch(AssessmentSliceActions.mutateOnGoingId(ongoingId));
          const tabId = await generateUniqueId();
          if (tabId) {
            const body = {
              ongoingId: ongoingId,
              scheduleId: scheduleId,
              sessionId: tabId
            };
            const res1 = await updateTabId(body);
            dispatch(AssessmentSliceActions.mutateVisitorId(tabId));
          }
        }
      }

      const proctoringStarted = await startProctoring();
      if (videoProctoring && !proctoringStarted) {
        setError(
          failureReasonMessage ||
            t("assessment.systemCheck.unableToStartSecureProctoring")
        );
        setShowErrorModal(true);
        return;
      }

      dispatch(AssessmentSliceActions.mutateShowWarningModal(false));
      dispatch(AssessmentSliceActions.mutateShowProctoringVideoPreview(true));
      dispatch(AssessmentSliceActions.mutateAssessmentHasStarted(true));
      dispatch(
        AssessmentSliceActions.mutateInitialProctoringVideoPreview(true)
      );
      if (browserProctoring || videoProctoring) requestFullScreen(dispatch);
    } catch (err: unknown) {
      console.error(err);
      const temp = err as {
        title?: string;
        description?: string;
        err?: { data?: { error?: string[] } };
      };
      const rawErrList = temp?.err?.data?.error;
      const errorTitle = temp?.title ?? t("assessment.somethingWentWrong");
      const errList: string[] = (rawErrList ?? []).map(
        (el: string) => el.split("_")[0]
      );
      if (showErrors)
        showErrors(errorTitle, errList, "error", temp?.description);
    }
  };

  const clickHandler = async () => {
    if (inResumeScreen) {
      await startAssessmentHandler(inResumeScreen);
      return;
    }
    if (stepperFormActiveStep === 3 || stepperFormActiveStep === 2) {
      if (tncEnabled) {
        if (stepperFormActiveStep === 2)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(3));
        else dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(4));
      } else {
        await startAssessmentHandler(false);
      }
    }
  };

  useEffect(() => {
    void getDevices();
  }, [getDevices]);

  useEffect(() => {
    if (processStatus !== "Calibrating") {
      setCalibrationTimeout(false);
      return;
    }

    // Show fallback help only if calibration keeps running for too long.
    const timeoutId = setTimeout(() => {
      setCalibrationTimeout(true);
    }, 40000);

    return () => clearTimeout(timeoutId);
  }, [processStatus]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    // 15 sec timer
    if (selectedCamera && videoPreviewLoader) {
      timer = setTimeout(() => {
        setDelayedMessage(true);
      }, 15000);
    } else {
      setDelayedMessage(false);
    }

    return () => clearTimeout(timer);
  }, [selectedCamera, videoPreviewLoader]);

  return (
    <div>
      {/*Status Indicator  */}
      <div className="mb-10 pt-3">
        <CalibrationProcessStatusAlert
          status={processStatus as ProcessStatus}
          failureReason={failureReason}
          timeout={calibrationTimeout}
        />
      </div>
      <div className="w-full overflow-hidden h-[340px] rounded-3xl">
        {selectedCamera && (
          <div className={`${videoPreviewLoader ? "hidden" : "block"}`}>
            <video
              ref={registerPreviewSink}
              autoPlay
              className="object-cover w-full h-full "
              disablePictureInPicture
              disableRemotePlayback
            />
          </div>
        )}
        {selectedCamera && videoPreviewLoader && (
          <CameraPreviewLoader
            className="rounded-xl"
            showDelayedMessage={delayedMessage}
          />
        )}
        {!selectedCamera && (
          <div className="flex items-center justify-center w-full h-full bg-bkg-dark">
            <GetHeroIcon
              className="w-8 h-8 text-secondary"
              name="ExclamationTriangleIcon"
              variant="outline"
            />
          </div>
        )}
      </div>
      <div className="flex flex-col items-start justify-between gap-10 mt-6 lg:flex-row lg:items-center lg:justify-around empty:mt-3">
        {/* Select Camera */}
        {cameraDevices.length > 0 && processStatus !== "Calibrating" && (
          <div className="flex items-center w-[calc(85%-20px)] gap-3">
            <GetHeroIcon
              name="VideoCameraIcon"
              className="w-5 h-5 text-secondary"
            />
            <div className="w-full">
              <SelectCustom
                options={cameraDevices.map((device) => ({
                  id: device.deviceId,
                  name:
                    device.label || t("assessment.systemCheck.unnamedCamera"),
                  value: device.deviceId
                }))}
                setterFunction={(e: ChangeEvent<HTMLSelectElement>) => {
                  dispatch(
                    AssessmentSliceActions.mutateMediaDevices(e.target.value)
                  );
                }}
              />
            </div>
          </div>
        )}
      </div>
      <div
        className="text-sm font-medium text-blue-500 cursor-pointer mt-5"
        onClick={() => setShowCalibrationIssueModal(!showCalibrationIssueModal)}
      >
        {t("assessment.systemCheck.facingCalibrationIssues")}
      </div>
      {/* Error Messages */}
      {error && <div className="mt-10 text-center text-red-500">{error}</div>}

      {processStatus === "Calibrating" && (
        <div className="mt-6 font-medium text-center text-secondary">
          {t("assessment.systemCheck.lookAtCenter")}
        </div>
      )}
      {failureReasonMessage && sessionState === "recoverable_error" && (
        <div className="mt-6 font-medium text-center text-amber-600">
          {failureReasonMessage}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-5 mt-10 md:justify-end">
        {!inResumeScreen && (
          <Button
            type="button"
            variant="outlined"
            buttonColor="secondary"
            onClick={() => {
              if (setActiveStep) setActiveStep("previous");
            }}
            disabled={sessionState === "calibrating"}
          >
            {t("assessment.stepper.previousStep")}
          </Button>
        )}
        {(canCalibrate || sessionState === "calibrating") && (
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              void startCalibration();
            }}
            buttonColor="primary"
            disabled={
              !selectedCamera ||
              videoPreviewLoader ||
              sessionState === "calibrating" ||
              !canCalibrate
            }
          >
            {canCalibrate && (
              <span>{t("assessment.systemCheck.calibrate")}</span>
            )}
            {sessionState === "calibrating" && (
              <span>{t("assessment.systemCheck.calibrating")}</span>
            )}
          </Button>
        )}
        {canRetry && (
          <Button
            type="button"
            variant="contained"
            buttonColor="secondary"
            onClick={() => {
              void retryProctoring();
            }}
          >
            {t("assessment.wrapper.retryProctoring")}
          </Button>
        )}
        {canStart && (
          <Button
            type="button"
            variant="contained"
            buttonColor="primary"
            onClick={clickHandler}
            disabled={isLoading}
          >
            {tncEnabled
              ? inResumeScreen
                ? t("assessment.stepper.resumeAssessment")
                : t("assessment.stepper.nextStep")
              : inResumeScreen
                ? t("assessment.stepper.resumeAssessment")
                : t("assessment.stepper.startAssessment")}
          </Button>
        )}
      </div>
      <Modal
        open={showErrorModal}
        setOpen={setShowErrorModal}
        dialogueClassName="max-w-[500px]"
        childDialogTitle={t("assessment.systemCheck.accessDenied")}
        children={
          <div className="mt-10 text-secondary">
            <p>{error}</p>
            <div className="flex justify-end mt-10">
              <Button
                buttonColor="primary"
                variant="contained"
                onClick={() => setShowErrorModal(false)}
              >
                {t("assessment.stepper.okay")}
              </Button>
            </div>
          </div>
        }
      />
      <Modal
        open={showCalibrationIssueModal}
        children={<CalibrationInfoModal />}
        setOpen={setShowCalibrationIssueModal}
        dialogueClassName="max-w-[700px]"
        childDialogTitle={t("assessment.systemCheck.unableToCalibrateTitle")}
      />
    </div>
  );
};

export default SystemCheck;
